import { useState } from "react"

import type {
  PricingKindBundle,
  PricingVppCatalog,
  PricingVppProgram,
  VppStepBundle,
} from "./pricing-kind-bundle"
import {
  PricingLoadError,
  PricingLoading,
  PricingStepCloseFooter,
  PricingStepDrawer,
  PricingStepFooter,
  ToggleCard,
  ToggleCardGrid,
  ToggleCardTitle,
} from "./pricing-step-chrome"
import { usePricingSaveGuard } from "./use-pricing-save-guard"
import { formatUsdExact } from "../../BatteryOnlyProposal/view-helpers"

export const VPP_ESTIMATE_ONLY = `Selecting a program adds its estimated benefit to this proposal. It does not enroll the homeowner.`
export const VPP_SAVE_FAILED = `Unable to save VPP programs. Your changes are still here.`
export const VPP_LOAD_FAILED = `Unable to load VPP programs`

export const vppRemovedProgramNotice = (programName: string): string =>
  `${programName} is no longer available for this proposal`

const selectedIdsOf = (
  programs: readonly PricingVppProgram[],
): ReadonlySet<string> =>
  new Set(
    programs.filter((program) => program.selected).map((program) => program.id),
  )

const VppProgramCard = ({
  program,
  estimationMonths,
  selected,
  disabled,
  onToggle,
}: {
  program: PricingVppProgram
  estimationMonths: number
  selected: boolean
  disabled: boolean
  onToggle: () => void
}): JSX.Element => (
  <ToggleCard
    selected={selected}
    disabled={disabled}
    toggleTestId={`vpp-program-toggle-${program.id}`}
    onToggle={onToggle}
    title={<ToggleCardTitle>{program.name}</ToggleCardTitle>}
  >
    {program.description ? (
      <p className="text-[length:var(--step--2)] leading-snug text-foreground-soft">
        {program.description}
      </p>
    ) : null}
    <div className="flex flex-col items-end gap-0.5">
      <span className="text-[length:var(--step-0)] font-semibold text-bp-accent tabular-nums">
        {formatUsdExact(program.monthlyBenefit)}
        <span className="text-[length:var(--step--2)] font-normal">/mo</span>
      </span>
      {/* The period is named beside the number: the server's estimation
          period, not the Loan Product term. */}
      <span className="text-[length:var(--step--2)] text-muted tabular-nums">
        {formatUsdExact(program.totalBenefit)} over {estimationMonths / 12} years
      </span>
    </div>
  </ToggleCard>
)

const VppProgramsCatalog = ({
  bundle,
  proposalId,
  catalog,
  onClose,
}: {
  bundle: VppStepBundle
  proposalId: string
  catalog: PricingVppCatalog
  onClose: () => void
}): JSX.Element => {
  const { programs, removedPrograms, estimationMonths } = catalog
  const savePrograms = bundle.useSave(proposalId)
  const guard = usePricingSaveGuard({ onClose, savedFeedback: true })
  // Draft lives only here; unmounting (any close path) discards it.
  const [draft, setDraft] = useState<ReadonlySet<string> | null>(null)
  const selected = draft ?? selectedIdsOf(programs)
  const saving = savePrograms.isLoading
  const locked = saving || guard.justSaved

  const toggle = (id: string): void => {
    savePrograms.reset()
    const next = new Set(selected)
    if (next.has(id)) {
      next.delete(id)
    } else {
      next.add(id)
    }
    setDraft(next)
  }

  const submit = (): void => {
    if (locked || !guard.start()) return
    savePrograms.run(
      {
        selectedVppProgramIds: programs
          .filter((program) => selected.has(program.id))
          .map((program) => program.id),
        // Drains the server's pending removals for the set this drawer
        // read, so a removal recorded while it was open is not swallowed.
        acknowledgedRemovalIds: removedPrograms.map(
          (program) => program.vppProgramId,
        ),
      },
      {
        onSettled: guard.release,
        onSuccess: () => {
          setDraft(null)
          guard.succeed()
        },
      },
    )
  }

  const removalNotices =
    bundle.showRemovedPrograms && removedPrograms.length > 0 ? (
      <ul
        role="status"
        data-testid="vpp-removed-programs"
        className="mb-4 flex list-none flex-col gap-1 rounded-md border border-bp-gold/40 bg-bp-gold/10 px-3 py-2 text-[length:var(--step--2)] text-bp-gold"
      >
        {removedPrograms.map((program) => (
          <li key={program.vppProgramId}>
            {vppRemovedProgramNotice(program.programName)}
          </li>
        ))}
      </ul>
    ) : null

  return (
    <>
      {removalNotices}
      {programs.length === 0 ? (
        <div role="status" className="flex flex-col gap-2">
          <p className="text-[length:var(--step--1)] text-foreground">
            {bundle.copy.empty}
          </p>
          <p className="text-[length:var(--step--2)] text-muted">
            {bundle.copy.emptyDetail}
          </p>
        </div>
      ) : (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-y-auto">
          <p className="text-[length:var(--step--2)] text-muted">
            {VPP_ESTIMATE_ONLY}
          </p>
          <ToggleCardGrid
            testId="vpp-programs-cards-container"
            className="grid auto-rows-min grid-cols-1 gap-4 sm:grid-cols-2"
          >
            {programs.map((program) => (
              <VppProgramCard
                key={program.id}
                program={program}
                estimationMonths={estimationMonths}
                selected={selected.has(program.id)}
                disabled={locked}
                onToggle={() => toggle(program.id)}
              />
            ))}
          </ToggleCardGrid>
        </div>
      )}

      <PricingStepFooter
        onClose={onClose}
        error={
          savePrograms.isError
            ? {
                message: bundle.describeSaveError(savePrograms.error),
                onRetry: submit,
              }
            : null
        }
        savedMessage={guard.justSaved ? `VPP programs saved` : null}
        save={{
          label: `Save VPP programs`,
          saving,
          disabled: locked,
          onSave: submit,
        }}
      />
    </>
  )
}

/**
 * VPP Programs: estimated benefits a seller adds to the proposal. Selecting
 * never enrolls the homeowner and does not move the price.
 */
export const PricingVppProgramsStep = ({
  kind,
  proposalId,
  onClose,
}: {
  kind: PricingKindBundle
  proposalId: string
  onClose: () => void
}): JSX.Element => {
  const bundle = kind.vpp
  const programsQuery = bundle.usePrograms(proposalId)
  const catalog = programsQuery.isSuccess ? programsQuery.data : undefined

  return (
    <PricingStepDrawer title="VPP Programs" onClose={onClose}>
      {programsQuery.isLoading ? (
        <PricingLoading>Loading VPP programs</PricingLoading>
      ) : null}
      {programsQuery.isError ? (
        <PricingLoadError
          message={VPP_LOAD_FAILED}
          onRetry={() => void programsQuery.refetch()}
        />
      ) : null}

      {catalog ? (
        <VppProgramsCatalog
          bundle={bundle}
          proposalId={proposalId}
          catalog={catalog}
          onClose={onClose}
        />
      ) : (
        <PricingStepCloseFooter onClose={onClose} />
      )}
    </PricingStepDrawer>
  )
}
